<template lang="pug">
  .news-container
    Pagination.pagination-bar(
      v-if="totalCount > 0"
      :pagination="uiPagination"
      :available-per-page="true"
      :list-upside="false"
      @selectPage="onSelectPage"
      @selectPerPage="onSelectPerPage"
    )
    h1.news-title お知らせ一覧
    .news-list(v-if="paginatedNews.length")
      .news-card(
        v-for="news in paginatedNews"
        :key="news.id"
        tabindex="0"
        :class="{ 'is-unread': !news.is_read, 'is-recent-unread': news.is_recent_unread }"
        @click="openModal(news)"
        @keydown.enter="openModal(news)"
        role="button"
        :aria-label="`『${news.title}』の詳細を開く`"
      )
        .news-content
          .news-meta-row
            .news-date {{ formatDate(news.published_on) }}
            span.news-fresh-badge(v-if="news.is_recent_unread") 新着
            span.news-status-badge.unread(v-if="!news.is_read") 未読
          h2.news-item-title {{ news.title }}
    .news-empty(v-else-if="!isLoading")
      | 現在、お知らせはありません。
    .news-loading(v-else)
      | 読み込み中です…

    Pagination.pagination-bar(
      v-if="totalCount > 50"
      :pagination="uiPagination"
      :available-per-page="true"
      :list-upside="false"
      @selectPage="onSelectPage"
      @selectPerPage="onSelectPerPage"
    )

    transition(name="modal-fade")
      .news-modal-backdrop(
        v-if="selectedNews"
        role="dialog"
        aria-modal="true"
        aria-labelledby="news-modal-title"
        @click.self="closeModal"
      )
        transition(name="modal-zoom")
          .news-modal(
            v-show="selectedNews"
            ref="modal"
            :tabindex="0"
          )
            header.news-modal__header
              .news-header
                .news-modal-meta
                  .news-date-badge {{ formatDate(selectedNews.published_on) }}
                  span.news-fresh-badge(v-if="selectedNews.is_recent_unread") 新着
                  span.news-status-badge.unread(v-if="!selectedNews.is_read") 未読
                h2#news-modal-title.news-modal__title {{ selectedNews.title }}
              button.news-modal__close(
                @click="closeModal"
                aria-label="閉じる"
              ) ×
            section.news-modal__body
              .news-body(v-if="selectedNews && selectedNews.body" v-html="selectedNews.body")
            footer.news-modal__footer
              button.news-modal__action(@click="closeModal") 閉じる
</template>

<script>
import { mapGetters } from 'vuex'
import Pagination from './parts/Pagination'

export default {
  name: 'News',
  components: { Pagination },
  data () {
    return {
      selectedNews: null,
      lastFocusedEl: null,
      currentPage: 1,
      perPage: 50,
      isLoading: true,
      oemIdentifier: null,
    }
  },
  computed: {
    ...mapGetters({
      newsList: 'news/index',
    }),
    filteredNews () {
      return this.newsList.filter(n => n.is_enabled)
    },
    totalCount () {
      return this.filteredNews.length
    },
    totalPages () {
      return Math.max(1, Math.ceil(this.totalCount / this.perPage))
    },
    paginatedNews () {
      const start = (this.currentPage - 1) * this.perPage
      return this.filteredNews.slice(start, start + this.perPage)
    },
    uiPagination () {
      return {
        currentPage: this.currentPage,
        currentPerPage: this.perPage,
        totalCount: this.totalCount,
        totalPages: this.totalPages,
        prevPage: this.currentPage > 1 ? this.currentPage - 1 : null,
        nextPage: this.currentPage < this.totalPages ? this.currentPage + 1 : null,
      }
    },
  },
  watch: {
    totalPages (next) {
      if (this.currentPage > next) this.currentPage = next
    },
  },
  methods: {
    formatDate (dateStr) {
      if (!dateStr) return ''
      const date = new Date(dateStr)
      const y = date.getFullYear()
      const m = (`0${date.getMonth() + 1}`).slice(-2)
      const d = (`0${date.getDate()}`).slice(-2)
      return `${y}年${m}月${d}日`
    },
    onSelectPage (page) {
      const p = Number(page)
      if (!Number.isNaN(p) && p >= 1 && p <= this.totalPages) {
        this.currentPage = p
        this.$nextTick(() => window.scrollTo(0, 0))
      }
    },
    onSelectPerPage (value) {
      const v = Number(value)
      if (!Number.isNaN(v) && v > 0) {
        this.perPage = v
        this.currentPage = 1
        this.$nextTick(() => window.scrollTo(0, 0))
      }
    },
    async openModal (news) {
      this.lastFocusedEl = document.activeElement
      this.selectedNews = news
      document.documentElement.style.overflow = 'hidden'
      this.$nextTick(() => {
        const el = this.$refs.modal
        if (el) el.focus()
        window.addEventListener('keydown', this.onKeydown)
      })

      if (!news.is_read) {
        try {
          await this.$store.dispatch('news/read', { id: news.id, oemIdentifier: this.oemIdentifier })
          const selectedNews = this.newsList.find(n => n.id === news.id)
          if (selectedNews) this.selectedNews = selectedNews
        } catch (e) {
          this.$store.dispatch('common/pushToastError', { error: e })
        }
      }
    },
    closeModal () {
      window.removeEventListener('keydown', this.onKeydown)
      this.selectedNews = null
      document.documentElement.style.overflow = ''
      this.$nextTick(() => {
        if (this.lastFocusedEl && this.lastFocusedEl.focus) this.lastFocusedEl.focus()
      })
    },
    onKeydown (e) {
      if (e.key === 'Escape') this.closeModal()
    },
  },
  async mounted () {
    try {
      await this.$store.dispatch('client/fetch')

      const clientState = this.$store.state.client
      const detail = clientState && clientState.detail
      this.oemIdentifier = detail && detail.oem_partner_identifier

      await this.$store.dispatch('news/fetchIndex', { oemIdentifier: this.oemIdentifier })
    } finally {
      this.isLoading = false
    }
  },
}
</script>



// WEBPACK FOOTER //
// src/components/News.vue